/**
 * SortableFormulaChip — draggable formula token (grip + label + remove)
 * For Media Devoted | Created By Ciro Autiero
 */

import { memo } from 'react';
import type { FC } from 'react';
import { GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { cn } from '@/lib/utils';

import { chipKindClassName } from '../BlockTokenChip';
import type { ChipKind } from '../blocks.tokens';

export interface SortableFormulaChipProps {
  id: string;
  kind: ChipKind;
  display: string;
  onRemove: () => void;
}

const SortableFormulaChip: FC<SortableFormulaChipProps> = ({
  id,
  kind,
  display,
  onRemove,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-md border px-1 py-0.5 text-xs font-medium tabular-nums shadow-sm',
        chipKindClassName(kind),
        isDragging && 'z-20 cursor-grabbing opacity-95 ring-2 ring-primary/30',
      )}
    >
      <button
        type="button"
        className={cn(
          'flex touch-none cursor-grab items-center justify-center rounded px-0.5 text-muted-foreground hover:bg-black/5',
          isDragging && 'cursor-grabbing',
        )}
        aria-label={`Drag to reorder ${display}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5 shrink-0" aria-hidden />
      </button>
      <span className="max-w-[12rem] truncate px-0.5">{display}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${display}`}
        className="rounded px-1 text-[10px] text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      >
        ×
      </button>
    </div>
  );
};

export default memo(SortableFormulaChip);
